/**
 * counterSignals — In-trade counter-signal detector (Phase 19).
 *
 * Pure function. Given a trade and the OHLCV bars covering its lifetime,
 * detects signals that fired AGAINST the position direction between entry
 * and exit. The output drives:
 *
 *   - The `inTradeCounterSignal` Coach analyzer (clusters signals across
 *     closed trades; surfaces patterns like "when 15m closes against your
 *     shorts, WR drops from 53% to 28%")
 *   - Trade card flag chips (visual marker per closed trade)
 *   - Live insights whisper (real-time warning while still in trade)
 *
 * V1 signal types:
 *   - 'ema-close-against' — a bar closes on the wrong side of an EMA
 *     for the trade's direction (long → close below; short → close above)
 *
 * Future signal types (placeholder seam):
 *   - 'vwap-rejection'
 *   - 'key-level-break'
 *   - 'higher-low-broken' / 'lower-high-broken'
 *
 * Design choices:
 *
 *   1. ONE event per (timeframe, EMA period). The first cross-against is
 *      the actionable moment. Subsequent closes on the wrong side aren't
 *      separate signals — they're persistence. Deduping at this layer
 *      keeps the metrics layer clean.
 *
 *   2. Cross-only, not "currently on wrong side". A bar that opened
 *      already below the EMA (long trade) and stayed below didn't *cross*
 *      against you — that's a regime condition that should have been an
 *      entry-time counter-confluence, not an in-trade signal. We only
 *      fire when the prior bar's close was on the RIGHT side AND the
 *      current bar's close is on the WRONG side.
 *
 *   3. Min-distance gate. A close 0.25 ticks past the EMA is noise. The
 *      `minDistancePoints` opt (default 0.5 points = 2 ticks on MNQ)
 *      filters those out. The trader's eye does this naturally; the
 *      detector needs an explicit threshold.
 *
 *   4. Watch only the slow/fast EMAs that traders actually anchor to.
 *      Default [9, 21, 50] — short-term momentum, mid-term trend, the
 *      slower regime line. 5 and 200 are noisier / rarer to anchor to;
 *      callers can opt in.
 *
 *   5. Per-timeframe — caller supplies a map of timeframe → bars.
 *      Detection runs independently per timeframe. A 5m close-against
 *      is different signal from a 15m close-against — both can fire on
 *      the same trade and both are useful data points.
 */

import { computeEmas } from './emas.js';
import { computeVWAP } from './vwap.js';
import { getExecutionInstantMs } from '../timeService.js';

export const COUNTER_SIGNAL_TYPES = Object.freeze({
  EMA_CLOSE_AGAINST: 'ema-close-against',
  VWAP_CLOSE_AGAINST: 'vwap-close-against',
  LEVEL_BREAK: 'level-break',
});

// Default EMA periods to scan per timeframe. Keep this short — fewer
// periods = less noise + faster analysis. Traders can override via opts.
export const DEFAULT_EMA_PERIODS_TO_WATCH = Object.freeze([9, 21, 50]);

// Minimum distance (in instrument points) past the EMA before a close
// counts as "decisively" against the position. 0.5 points = 2 ticks on
// MNQ — enough to filter tick noise without losing signal.
export const DEFAULT_MIN_DISTANCE_POINTS = 0.5;

// Default timeframes a caller is expected to provide. We default to
// scanning 5m and 15m if both are present. 1m is excluded because it
// fires constantly and isn't actionable for trading decisions; 1h+ is
// excluded because most in-trade signals close out before an hour.
export const DEFAULT_TIMEFRAMES_TO_SCAN = Object.freeze(['5m', '15m']);

// Timeframe label → seconds. Used by bar-gap guards in the scanners:
// the level-break scanner verifies the chosen entry bar actually covers
// the entry timestamp (otherwise a missing bar in the data feed would
// cause us to use a stale pre-gap bar as the reference level — see
// "false-fire on missing bar" regression below).
const TF_TO_SECONDS = Object.freeze({
  '1m': 60, '3m': 180, '5m': 300, '15m': 900, '30m': 1800,
  '1h': 3600, '60m': 3600, '4h': 14400, '240m': 14400, '1d': 86400,
});

export const tfToSeconds = (tf) => TF_TO_SECONDS[tf] || null;

/**
 * Normalize a trade direction value to canonical 'long' | 'short' | null.
 * Tolerates case + common synonyms (`buy`/`sell`, `BUY`, `SHORT `). Returns
 * null for anything unrecognized so callers can early-exit safely.
 *
 * Why this matters: the detector's `direction === 'long' ? X : Y` ternaries
 * default to the SHORT branch for any non-`'long'` value. If a trade ever
 * arrives with `direction: 'buy'`, the detector would silently run short
 * logic on a long position and produce inverted signals. Hard-fail at the
 * top with normalization instead.
 */
export const normalizeDirection = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();
  if (s === 'long' || s === 'buy' || s === 'l') return 'long';
  if (s === 'short' || s === 'sell' || s === 's') return 'short';
  return null;
};

/**
 * Resolve the trade's entry + exit window to Unix seconds. Mirrors
 * maeMfe.resolveTradeWindow so the two analyzers walk identical windows.
 * Returns null when no usable entry time.
 */
const resolveTradeWindow = (trade) => {
  if (!trade?.date) return null;
  const entryMs = getExecutionInstantMs(trade.date, trade.entryTime);
  if (!entryMs) return null;

  let exitMs = null;
  if (trade.exitTime) {
    exitMs = getExecutionInstantMs(trade.date, trade.exitTime);
    // Defensive: skewed/typo exit before entry → treat as open
    if (!exitMs || exitMs < entryMs) exitMs = null;
  }

  return {
    entryTs: Math.floor(entryMs / 1000),
    exitTs: exitMs ? Math.floor(exitMs / 1000) : null,
  };
};

/**
 * Is the chosen entry bar actually the bar that contains entryTs?
 *
 * Bars are presumed to be OPEN-time stamped: a 5m bar at `time:T` spans
 * `[T, T + 300)`. If the bar SOURCE has a gap and the real entry bar is
 * missing from the array, the loop that picks "last bar with time ≤ entryTs"
 * silently grabs the PREVIOUS bar. The previous bar's open/high/low/close
 * have nothing to do with the price action at the entry moment, so any
 * structure-break signal derived from them is a fabrication.
 *
 * Returns true iff entryTs falls inside the entry bar's expected span.
 * False when the real entry bar is missing (gap in the data) or the
 * timeframe is unknown.
 */
const entryBarCoversEntryTs = (entryBar, timeframe, entryTs) => {
  if (!entryBar) return false;
  const periodSec = tfToSeconds(timeframe);
  if (!periodSec) return false;
  const start = Number(entryBar.time);
  if (!Number.isFinite(start)) return false;
  const end = start + periodSec;
  return entryTs >= start && entryTs < end;
};

/**
 * Is `close` decisively past `ema` against `direction`?
 *   long  → close BELOW ema by ≥ minDistance
 *   short → close ABOVE ema by ≥ minDistance
 */
const isCloseAgainst = (close, ema, direction, minDistance) => {
  if (!Number.isFinite(close) || !Number.isFinite(ema)) return false;
  if (direction === 'long') return (ema - close) >= minDistance;
  if (direction === 'short') return (close - ema) >= minDistance;
  return false;
};

/**
 * Scan bars for a VWAP close-against signal. For long: bar closes BELOW
 * VWAP (after prior close was above). For short: bar closes ABOVE VWAP.
 * VWAP is session-anchored — see vwap.js for boundary logic. Only fires
 * the first cross per (timeframe) — subsequent persistence isn't a fresh
 * signal.
 */
const scanTimeframeForVwapCross = (timeframe, bars, ctx) => {
  const { entryTs, exitTs, direction, minDistancePoints } = ctx;
  if (!Array.isArray(bars) || bars.length < 2) return [];

  // Bar period — used by the adjacency guard below. If unknown, skip
  // the adjacency check (defaults to original behavior, slightly looser).
  const periodSec = tfToSeconds(timeframe);
  const maxAdjacencyGap = periodSec ? periodSec * 1.5 : null;

  const vwapSeries = computeVWAP(bars);
  const events = [];
  let fired = false;

  for (let i = 1; i < bars.length; i++) {
    if (fired) break;
    const bar = bars[i];
    const prevBar = bars[i - 1];
    const t = Number(bar?.time);
    const tPrev = Number(prevBar?.time);
    if (!Number.isFinite(t)) continue;
    if (t < entryTs) continue;
    if (exitTs != null && t > exitTs) break;

    // Adjacency guard: a "cross" between two non-adjacent bars is
    // ambiguous — the actual cross may have happened during the
    // missing bars rather than at this boundary. Skip cross detection
    // here; later iterations with adjacent pairs will still fire.
    if (maxAdjacencyGap && Number.isFinite(tPrev) && (t - tPrev) > maxAdjacencyGap) continue;

    const close = Number(bar.close);
    const prevClose = Number(prevBar.close);
    if (!Number.isFinite(close) || !Number.isFinite(prevClose)) continue;

    const vwapNow = vwapSeries[i]?.value;
    const vwapPrev = vwapSeries[i - 1]?.value;
    if (!Number.isFinite(vwapNow) || !Number.isFinite(vwapPrev)) continue;

    const nowAgainst = isCloseAgainst(close, vwapNow, direction, minDistancePoints);
    if (!nowAgainst) continue;

    const prevAgainst = isCloseAgainst(prevClose, vwapPrev, direction, minDistancePoints);
    if (prevAgainst) continue;  // already on wrong side — not a fresh cross

    fired = true;
    const distancePoints = direction === 'long'
      ? (vwapNow - close)
      : (close - vwapNow);

    events.push({
      type: COUNTER_SIGNAL_TYPES.VWAP_CLOSE_AGAINST,
      timeframe,
      barTime: t,
      secondsIntoTrade: t - entryTs,
      close,
      vwap: vwapNow,
      distancePoints: Math.round(distancePoints * 100) / 100,
    });
  }
  return events;
};

/**
 * Scan bars for a level-break signal. v1 detects ENTRY BAR HIGH/LOW
 * breaks (the simplest, most universal "structure failed" signal):
 *   - LONG: a subsequent bar's LOW drops below the entry bar's LOW
 *   - SHORT: a subsequent bar's HIGH rises above the entry bar's HIGH
 *
 * The entry bar = the bar containing entryTs. We find it then look at all
 * subsequent bars within the window for the first break. Fires once.
 *
 * Future variants (when ready): prior swing low/high, HoD/LoD, session
 * pivots. All share the same shape — different reference levels.
 *
 * BUG GUARD — entry-bar containment:
 *   The bar source (JournifiBars AddOn → CSV → BarDataService) can drop
 *   bars (network glitches, NT restarts, write failures). When the bar
 *   that ACTUALLY covers entryTs is missing, the naive "last bar with
 *   time ≤ entryTs" loop silently picks the previous bar — whose
 *   high/low are unrelated to the price action at the entry moment.
 *   A subsequent bar's normal price recovery then looks like a 20+ point
 *   structure break, producing a phantom counter-signal whisper.
 *   We verify the chosen entry bar actually covers entryTs before using
 *   its reference level. On miss, bail out: false negative > false alarm.
 */
const scanTimeframeForLevelBreak = (timeframe, bars, ctx) => {
  const { entryTs, exitTs, direction, minDistancePoints } = ctx;
  if (!Array.isArray(bars) || bars.length < 2) return [];

  // Find the entry bar — the LAST bar whose time is ≤ entryTs.
  let entryIdx = -1;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    if (t <= entryTs) entryIdx = i;
    else break;
  }
  if (entryIdx < 0) return [];

  const entryBar = bars[entryIdx];

  // ── Containment guard (the fix for false-fire on missing bars) ────
  // Without this check, a gap in the bar source where the real entry
  // bar is absent produces a phantom break signal off the pre-gap bar's
  // high/low. See the regression test for the exact 06:49 MNQ trade
  // that exposed this in production.
  if (!entryBarCoversEntryTs(entryBar, timeframe, entryTs)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[counterSignals] Skipping ${timeframe} level-break scan: entry bar at ${entryBar?.time} ` +
      `does not cover entryTs ${entryTs} (likely missing bar in source feed).`
    );
    return [];
  }

  // Reference level — the level that, if broken, signals structure failure.
  // long: entry bar's LOW is "support" — break below = bad
  // short: entry bar's HIGH is "resistance" — break above = bad
  const refLevel = direction === 'long'
    ? Number(entryBar.low)
    : Number(entryBar.high);
  if (!Number.isFinite(refLevel)) return [];

  // Walk subsequent bars in window looking for break.
  for (let i = entryIdx + 1; i < bars.length; i++) {
    const bar = bars[i];
    const t = Number(bar?.time);
    if (!Number.isFinite(t)) continue;
    if (exitTs != null && t > exitTs) break;

    // For long: bar's LOW dipping below entry bar's LOW = break.
    // For short: bar's HIGH spiking above entry bar's HIGH = break.
    // We use the same minDistancePoints gate to filter wick-noise.
    const testValue = direction === 'long' ? Number(bar.low) : Number(bar.high);
    if (!Number.isFinite(testValue)) continue;

    const broken = direction === 'long'
      ? (refLevel - testValue) >= minDistancePoints
      : (testValue - refLevel) >= minDistancePoints;
    if (!broken) continue;

    const distancePoints = direction === 'long'
      ? (refLevel - testValue)
      : (testValue - refLevel);

    return [{
      type: COUNTER_SIGNAL_TYPES.LEVEL_BREAK,
      timeframe,
      barTime: t,
      secondsIntoTrade: t - entryTs,
      levelName: direction === 'long' ? 'entry-bar-low' : 'entry-bar-high',
      level: refLevel,
      tested: testValue,
      close: Number(bar.close),
      distancePoints: Math.round(distancePoints * 100) / 100,
    }];
  }

  return [];
};

/**
 * Scan bars on a single timeframe for EMA-close-against signals. Helper
 * for `detectInTradeCounterSignals` which fans out across all configured
 * timeframes. Returns an array of signal events for THIS timeframe only.
 */
const scanTimeframeForEmaCrosses = (timeframe, bars, ctx) => {
  const { entryTs, exitTs, direction, emaPeriods, minDistancePoints } = ctx;
  if (!Array.isArray(bars) || bars.length < 2) return [];

  // Bar period for the adjacency guard (skip cross detection on the
  // first bar after a gap — see scanTimeframeForVwapCross for rationale).
  const periodSec = tfToSeconds(timeframe);
  const maxAdjacencyGap = periodSec ? periodSec * 1.5 : null;

  // Compute all configured EMAs in one pass over the bars. Returns
  // { period: [{time,value}, ...] } parallel to `bars`.
  const emaSeries = computeEmas(bars, emaPeriods);

  // Window filter: include bars whose stamp is at or after entry, and at
  // or before exit (or no upper bound if trade is open). Index alignment
  // with the parallel EMA series is preserved.
  const events = [];

  // Track which (timeframe, period) keys have already fired — we only
  // record the FIRST cross-against per pair (see design choice #1).
  const firedKeys = new Set();

  for (let i = 1; i < bars.length; i++) {
    const bar = bars[i];
    const prevBar = bars[i - 1];
    const t = Number(bar?.time);
    const tPrev = Number(prevBar?.time);
    if (!Number.isFinite(t)) continue;
    if (t < entryTs) continue;
    if (exitTs != null && t > exitTs) break;

    // Adjacency guard — skip cross detection when bars[i-1] and bars[i]
    // are separated by a gap larger than the bar period (data dropouts,
    // session breaks). A "cross" inferred across the gap may have
    // happened during the missing window, so we can't time-stamp it
    // accurately. Subsequent adjacent pairs are still checked normally.
    if (maxAdjacencyGap && Number.isFinite(tPrev) && (t - tPrev) > maxAdjacencyGap) continue;

    const close = Number(bar.close);
    const prevClose = Number(prevBar.close);
    if (!Number.isFinite(close) || !Number.isFinite(prevClose)) continue;

    for (const period of emaPeriods) {
      const key = `${timeframe}|${period}`;
      if (firedKeys.has(key)) continue;

      const emaNow = emaSeries[period]?.[i]?.value;
      const emaPrev = emaSeries[period]?.[i - 1]?.value;
      if (!Number.isFinite(emaNow) || !Number.isFinite(emaPrev)) continue;

      const nowAgainst = isCloseAgainst(close, emaNow, direction, minDistancePoints);
      if (!nowAgainst) continue;

      // Cross detection: prior close must have been on the right side
      // (or AT the EMA — not yet decisively against). This filters out
      // bars that opened already on the wrong side (regime condition,
      // not a fresh signal).
      const prevAgainst = isCloseAgainst(prevClose, emaPrev, direction, minDistancePoints);
      if (prevAgainst) continue;

      firedKeys.add(key);
      const distancePoints = direction === 'long'
        ? (emaNow - close)
        : (close - emaNow);

      events.push({
        type: COUNTER_SIGNAL_TYPES.EMA_CLOSE_AGAINST,
        timeframe,
        emaPeriod: period,
        barTime: t,
        secondsIntoTrade: t - entryTs,
        close,
        ema: emaNow,
        distancePoints: Math.round(distancePoints * 100) / 100,
      });
    }
  }

  return events;
};

/**
 * Main entry point.
 *
 * @param {Object} trade — needs at minimum date, entryTime, direction, stopLossPoints (optional, for R)
 * @param {Object} options
 * @param {Object} [options.htfBars]            { '5m': bars[], '15m': bars[], ... }
 * @param {Array}  [options.emaPeriods]         EMAs to watch (default [9,21,50])
 * @param {number} [options.minDistancePoints]  Distance gate (default 0.5)
 * @param {Array}  [options.timeframes]         Subset of htfBars keys to scan (default ['5m','15m'])
 * @returns {Array<{ type, timeframe, emaPeriod, barTime, secondsIntoTrade, close, ema, distancePoints, distanceR? }>}
 *          Sorted by barTime ascending. Empty array on bad input (never null).
 */
export const detectInTradeCounterSignals = (trade, options = {}) => {
  if (!trade) return [];
  // Use the canonical normalizer so 'buy'/'sell' (or upper-case 'LONG')
  // map to the right branch instead of falling through the detector's
  // ternary and silently producing inverted signals.
  const direction = normalizeDirection(trade.direction);
  if (!direction) return [];

  const window = resolveTradeWindow(trade);
  if (!window) return [];

  const {
    htfBars = {},
    emaPeriods = DEFAULT_EMA_PERIODS_TO_WATCH,
    minDistancePoints = DEFAULT_MIN_DISTANCE_POINTS,
    timeframes = DEFAULT_TIMEFRAMES_TO_SCAN,
  } = options;

  // Filter to timeframes that we actually have bars for. Quietly skip
  // missing timeframes — callers may only have 5m available, that's fine.
  const tfsToScan = timeframes.filter(tf => Array.isArray(htfBars[tf]) && htfBars[tf].length > 1);
  if (tfsToScan.length === 0) return [];

  // Dedupe + sanitize EMA periods (matches computeEmas's own cleaner)
  const cleanPeriods = Array.from(new Set(
    (emaPeriods || []).map(p => Math.floor(Number(p))).filter(p => Number.isFinite(p) && p >= 1)
  )).sort((a, b) => a - b);
  if (cleanPeriods.length === 0) return [];

  const ctx = {
    entryTs: window.entryTs,
    exitTs: window.exitTs,
    direction,
    emaPeriods: cleanPeriods,
    minDistancePoints,
  };

  const allEvents = [];
  for (const tf of tfsToScan) {
    // EMA close-against (the v1 signal)
    allEvents.push(...scanTimeframeForEmaCrosses(tf, htfBars[tf], ctx));
    // VWAP close-against (session-anchored)
    allEvents.push(...scanTimeframeForVwapCross(tf, htfBars[tf], ctx));
    // Level break (entry bar high/low)
    allEvents.push(...scanTimeframeForLevelBreak(tf, htfBars[tf], ctx));
  }

  // Enrich with R-multiple distance if we have stopLossPoints
  const slPts = Number(trade.stopLossPoints);
  if (Number.isFinite(slPts) && slPts > 0) {
    for (const ev of allEvents) {
      ev.distanceR = Math.round((ev.distancePoints / slPts) * 100) / 100;
    }
  }

  // Sort by barTime ascending so the analyzer / UI can show signals in
  // the order they happened
  allEvents.sort((a, b) => a.barTime - b.barTime);

  return allEvents;
};

/**
 * Stable signature for a counter-signal event. Used by the analyzer to
 * cluster identical signals across trades regardless of timing details.
 * Format depends on signal type:
 *   ema-close-against:  `ema-close-against|<timeframe>|<emaPeriod>`
 *   vwap-close-against: `vwap-close-against|<timeframe>`
 *   level-break:        `level-break|<timeframe>|<levelName>`
 */
export const signalSignature = (event) => {
  if (!event) return '';
  switch (event.type) {
    case COUNTER_SIGNAL_TYPES.EMA_CLOSE_AGAINST:
      return `${event.type}|${event.timeframe || ''}|${event.emaPeriod || ''}`;
    case COUNTER_SIGNAL_TYPES.VWAP_CLOSE_AGAINST:
      return `${event.type}|${event.timeframe || ''}`;
    case COUNTER_SIGNAL_TYPES.LEVEL_BREAK:
      return `${event.type}|${event.timeframe || ''}|${event.levelName || ''}`;
    default:
      return `${event.type}|${event.timeframe || ''}|${event.emaPeriod || ''}`;
  }
};

/**
 * Human-readable label for a counter-signal event. Used in observation
 * detail strings and trade card chips.
 *   "15m close vs 9 EMA"
 *   "5m close vs VWAP"
 *   "5m broke entry-bar low"
 */
export const signalLabel = (event) => {
  if (!event) return '';
  switch (event.type) {
    case COUNTER_SIGNAL_TYPES.EMA_CLOSE_AGAINST:
      return `${event.timeframe} close vs ${event.emaPeriod} EMA`;
    case COUNTER_SIGNAL_TYPES.VWAP_CLOSE_AGAINST:
      return `${event.timeframe} close vs VWAP`;
    case COUNTER_SIGNAL_TYPES.LEVEL_BREAK:
      return `${event.timeframe} broke ${event.levelName || 'level'}`;
    default:
      return event.type;
  }
};
